import React from 'react'
import BulletPoint from 'scenes/Dashboard/components/BulletPoint'
import {
  getPerformanceIndicatorLabel,
  isMonetaryType,
  isRangeType,
} from 'scenes/Dashboard/data/performanceIndicators/helpers'
import {
  PerformanceIndicator,
  PerformanceIndicatorType,
  Range,
} from 'scenes/Dashboard/data/performanceIndicators/types'
import { makeStyles } from 'tss-react/mui'
import { format } from 'utils-next/dates'
import { groupCurrency } from 'utils/currency'

import { Typography } from '@mui/material'

type PerformanceIndicatorViewProps = {
  key?: React.Attributes['key']
  performanceIndicator: PerformanceIndicator
  displayLabel?: boolean
  dateFormat?: string
}

export const PerformanceIndicatorView = ({
  performanceIndicator,
  displayLabel = true,
  dateFormat = 'yyyy/MM',
}: PerformanceIndicatorViewProps) => {
  const { classes } = useStyles()
  if (!performanceIndicator) {
    return null
  }
  const { value, type, measuredOn } = performanceIndicator

  const label = getPerformanceIndicatorLabel(type)

  return (
    <BulletPoint
      className={classes.bulletPoint}
      item={
        <>
          {displayLabel && <Typography>{label} – </Typography>}
          <Typography variant="body2">
            {format(new Date(measuredOn), dateFormat)}
            {' – '}
            {getPerformanceIndicatorValue(value, type)}
          </Typography>
        </>
      }
    />
  )
}

function getPerformanceIndicatorValue(value: string | Range | number, type: PerformanceIndicatorType) {
  const isRange = isRangeType(type)
  const isMonetary = isMonetaryType(type)
  if (isRange && !!value) {
    return getRangeValueLabel(value as Range)
  } else if (isMonetary) {
    return getNumberValueLabel(value as number, isMonetary)
  } else {
    return value as string
  }
}

const getRangeValueLabel = (value: Range) => (
  <>
    {getNumberValueLabel(value.min, false)}
    {value.max ? <> to {getNumberValueLabel(value.max, false)}</> : <> minimum</>}
  </>
)

const getNumberValueLabel = (value: number, isMonetary: boolean) => (
  <span>{groupCurrency(value, isMonetary ? 'USD' : undefined)}</span>
)

const useStyles = makeStyles()({
  bulletPoint: { paddingTop: 0, paddingBottom: 0 },
})
